// src/pages/PublicSharedCollection.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
   Box,
   Typography,
   Card,
   CardMedia,
   CardContent,
   Button,
   Grid,
   CircularProgress,
   Alert,
   Container,
   Chip,
   Dialog,
   DialogContent,
   DialogTitle,
   DialogActions,
   IconButton,
   Paper,
   TextField,
   Fab,
} from '@mui/material';
import {
   Close as CloseIcon,
   GetApp as DownloadIcon,
   PersonAdd as PersonAddIcon,
   Login as LoginIcon,
   Email as EmailIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { SharedCollection } from '../types/SharedCollection';
import SharedCollectionService from '../services/sharedCollectionService';
import { useUser } from '../contexts/UserContext';
import PhotoLikeButton from '../components/common/PhotoLikeButton';

const PublicSharedCollection: React.FC = () => {
   const { shareToken } = useParams<{ shareToken: string }>();
   const navigate = useNavigate();
   const { currentUser, isLoading: userLoading } = useUser();

   // Extract email from URL parameters
   const urlParams = new URLSearchParams(window.location.search);
   const emailFromUrl = urlParams.get('email');
   const [collection, setCollection] = useState<SharedCollection | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
   const [hasViewedBefore, setHasViewedBefore] = useState(false);
   const [showEmailDialog, setShowEmailDialog] = useState(false);
   const [viewerEmail, setViewerEmail] = useState('');
   const [emailError, setEmailError] = useState('');

   useEffect(() => {
      if (shareToken) {
         console.log('🔍 ShareToken from URL:', shareToken);

         // Wait for user authentication to complete loading
         if (userLoading) {
            console.log('Waiting for user authentication to complete...');
            return;
         }

         console.log('🔐 Auth state:', {
            currentUser: !!currentUser,
            userLoading,
            emailFromUrl: emailFromUrl || 'none',
         });

         // Check if we have email from URL (clicked from email link)
         if (emailFromUrl) {
            console.log('📧 Email from URL found, using for automatic view tracking...');
            // Use email from URL for view tracking, regardless of auth status
            fetchCollection(emailFromUrl);
         } else if (currentUser) {
            // User is registered and logged in - proceed to fetch collection
            console.log('✅ User registered and logged in, fetching collection...');
            fetchCollection();
         } else {
            // User not logged in and no email from URL - need to check registration
            console.log('❓ User not logged in, showing email dialog to check registration...');
            setLoading(false);
            setShowEmailDialog(true);
         }
      }
   }, [shareToken, currentUser, userLoading, navigate, emailFromUrl]);

   useEffect(() => {
      // Update recipient status when user views the collection
      if (collection && currentUser && !hasViewedBefore) {
         updateRecipientStatus('viewed');
         setHasViewedBefore(true);
      }
   }, [collection, currentUser, hasViewedBefore]);

   const fetchCollection = async (email?: string) => {
      try {
         setLoading(true);

         // Determine viewer email
         let viewerEmailToUse: string | undefined = email || viewerEmail;
         if (currentUser && currentUser.email) {
            viewerEmailToUse = currentUser.email;
         }

         // Only pass email if it's not empty
         const emailToPass =
            viewerEmailToUse && viewerEmailToUse.trim() ? viewerEmailToUse : undefined;

         console.log('🚀 Fetching collection with:', {
            shareToken,
            emailToPass,
            currentUser: !!currentUser,
            fullUrl: window.location.href,
         });

         const sharedCollection = await SharedCollectionService.getSharedCollectionByToken(
            shareToken!,
            emailToPass
         );

         if (!sharedCollection) {
            setError('Shared collection not found or has been removed.');
            return;
         }

         console.log('PublicSharedCollection - Fetched collection:', sharedCollection);
         console.log(
            'PublicSharedCollection - Photos with like data:',
            sharedCollection.photos?.map((p) => ({
               id: p.originalPhotoId,
               likeCount: p.likeCount,
               isLiked: p.isLiked,
            }))
         );
         setCollection(sharedCollection);
      } catch (error) {
         console.error('Error fetching shared collection:', error);
         setError('Failed to load shared collection');
      } finally {
         setLoading(false);
      }
   };

   const updateRecipientStatus = async (status: 'viewed') => {
      if (!collection || !currentUser) return;

      try {
         await SharedCollectionService.updateRecipientStatus(
            collection.id,
            currentUser.email!,
            status,
            currentUser.uid
         );
      } catch (error) {
         console.error('Error updating recipient status:', error);
      }
   };

   const handleJoinWetrippen = () => {
      navigate('/signup', {
         state: {
            invitedViaSharing: true,
            shareToken: shareToken,
         },
      });
   };

   const handleLogin = () => {
      navigate('/login', {
         state: {
            returnTo: `/shared/collection/${shareToken}`,
         },
      });
   };

   const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
   };

   const checkUserExistsAndRedirect = async (email: string) => {
      try {
         // Check if user exists using the auth service
         const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email }),
            }
         );

         const data = await response.json();

         if (data.exists) {
            // User exists but not logged in - redirect to login
            console.log('User exists, redirecting to login...');
            navigate('/login', {
               state: {
                  returnTo: `/shared/collection/${shareToken}`,
               },
            });
         } else {
            // User doesn't exist - redirect to register
            console.log('User does not exist, redirecting to register...');
            navigate('/register', {
               state: {
                  returnTo: `/shared/collection/${shareToken}`,
                  invitedViaSharing: true,
                  shareToken: shareToken,
               },
            });
         }
      } catch (error) {
         console.error('Error checking user existence:', error);
         // Default to register if we can't check
         navigate('/register', {
            state: {
               returnTo: `/shared/collection/${shareToken}`,
               invitedViaSharing: true,
               shareToken: shareToken,
            },
         });
      }
   };

   const handleEmailSubmit = async () => {
      setEmailError('');

      if (!viewerEmail.trim()) {
         setEmailError('Please enter your email address');
         return;
      }

      if (!validateEmail(viewerEmail)) {
         setEmailError('Please enter a valid email address');
         return;
      }

      setShowEmailDialog(false);
      setLoading(true);
      await checkUserExistsAndRedirect(viewerEmail);
   };

   const handleSkipEmail = () => {
      // If they skip, just redirect to register as a new user
      navigate('/register', {
         state: {
            returnTo: `/shared/collection/${shareToken}`,
            invitedViaSharing: true,
            shareToken: shareToken,
         },
      });
   };

   const renderPhotoGrid = () => {
      if (!collection || collection.photos.length === 0) return null;

      const photos = collection.photos;

      // Different layouts based on collection.layout
      switch (collection.layout) {
         case 'single-hero':
            return (
               <Box textAlign='center'>
                  <Card sx={{ maxWidth: 800, margin: '0 auto', cursor: 'pointer' }}>
                     <Box onClick={() => setSelectedPhotoIndex(0)}>
                        <CardMedia
                           component='img'
                           image={photos[0].url}
                           alt={photos[0].title}
                           sx={{ height: 300, objectFit: 'cover' }}
                        />
                     </Box>
                     <CardContent
                        sx={{
                           p: 1,
                           display: 'flex',
                           justifyContent: 'flex-start',
                           alignItems: 'center',
                        }}
                     >
                        <PhotoLikeButton
                           photoId={photos[0].originalPhotoId}
                           userId={currentUser?.uid || ''}
                           context={{ type: 'collection', id: collection?.id || '' }}
                           initialLikeCount={photos[0].likeCount || 0}
                           initialIsLiked={photos[0].isLiked || false}
                           size='small'
                           variant='compact'
                           showCount={true}
                           disabled={!currentUser?.uid}
                           onLikeChange={(isLiked, likeCount) => {
                              // Update the photo in the collection state
                              setCollection((prev) => {
                                 if (!prev) return prev;
                                 const updatedPhotos = prev.photos.map((p, idx) =>
                                    idx === 0 ? { ...p, isLiked, likeCount } : p
                                 );
                                 return { ...prev, photos: updatedPhotos };
                              });
                           }}
                        />
                     </CardContent>
                  </Card>
               </Box>
            );

         case 'grid-square':
         case 'grid-instagram':
            return (
               <Grid container spacing={1}>
                  {photos.map((photo, index) => (
                     <Grid item xs={6} sm={4} md={3} key={photo.id}>
                        <Card sx={{ cursor: 'pointer' }}>
                           <Box onClick={() => setSelectedPhotoIndex(index)}>
                              <CardMedia
                                 component='img'
                                 image={photo.thumbnailUrl}
                                 alt={photo.title}
                                 sx={{
                                    height: 120,
                                    objectFit: 'cover',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.05)' },
                                 }}
                              />
                           </Box>
                           <Box sx={{ p: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
                              <PhotoLikeButton
                                 photoId={photo.originalPhotoId}
                                 userId={currentUser?.uid || ''}
                                 context={{ type: 'collection', id: collection?.id || '' }}
                                 initialLikeCount={photo.likeCount || 0}
                                 initialIsLiked={photo.isLiked || false}
                                 size='small'
                                 variant='compact'
                                 showCount={true}
                                 disabled={!currentUser}
                                 onLikeChange={(isLiked, likeCount) => {
                                    // Update the photo in the collection state
                                    setCollection((prev) => {
                                       if (!prev) return prev;
                                       const updatedPhotos = prev.photos.map((p) =>
                                          p.originalPhotoId === photo.originalPhotoId
                                             ? { ...p, isLiked, likeCount }
                                             : p
                                       );
                                       return { ...prev, photos: updatedPhotos };
                                    });
                                 }}
                              />
                           </Box>
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            );

         case 'story-horizontal':
            return (
               <Box
                  display='flex'
                  gap={2}
                  sx={{
                     overflowX: 'auto',
                     pb: 2,
                     '&::-webkit-scrollbar': { height: 8 },
                     '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: 4,
                     },
                  }}
               >
                  {photos.map((photo, index) => {
                     console.log(`PublicShared Photo ${index}:`, {
                        id: photo.originalPhotoId,
                        likeCount: photo.likeCount,
                        isLiked: photo.isLiked,
                        fullPhoto: photo,
                     });
                     return (
                        <Card
                           key={photo.id}
                           sx={{
                              minWidth: 300,
                              cursor: 'pointer',
                              flexShrink: 0,
                           }}
                        >
                           <Box onClick={() => setSelectedPhotoIndex(index)}>
                              <CardMedia
                                 component='img'
                                 image={photo.url}
                                 alt={photo.title}
                                 sx={{ height: 250, objectFit: 'cover' }}
                              />
                           </Box>
                           <CardContent
                              sx={{
                                 p: 0.5,
                                 display: 'flex',
                                 justifyContent: 'flex-start',
                                 alignItems: 'center',
                              }}
                           >
                              <PhotoLikeButton
                                 photoId={photo.originalPhotoId}
                                 userId={currentUser?.uid || 'anonymous'}
                                 context={{ type: 'collection', id: collection?.id || '' }}
                                 initialLikeCount={photo.likeCount || 0}
                                 initialIsLiked={photo.isLiked || false}
                                 size='small'
                                 variant='compact'
                                 showCount={true}
                                 disabled={!currentUser}
                                 onLikeChange={(isLiked, likeCount) => {
                                    // Update the photo in the collection state
                                    setCollection((prev) => {
                                       if (!prev) return prev;
                                       const updatedPhotos = prev.photos.map((p) =>
                                          p.originalPhotoId === photo.originalPhotoId
                                             ? { ...p, isLiked, likeCount }
                                             : p
                                       );
                                       return { ...prev, photos: updatedPhotos };
                                    });
                                 }}
                              />
                           </CardContent>
                        </Card>
                     );
                  })}
               </Box>
            );

         default:
            // Default masonry-like layout
            return (
               <Grid container spacing={1}>
                  {photos.map((photo, index) => (
                     <Grid item xs={12} sm={6} md={4} key={photo.id}>
                        <Card sx={{ cursor: 'pointer' }}>
                           <Box onClick={() => setSelectedPhotoIndex(index)}>
                              <CardMedia
                                 component='img'
                                 image={photo.url}
                                 alt={photo.title}
                                 sx={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: 300,
                                    objectFit: 'cover',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.02)' },
                                 }}
                              />
                           </Box>
                           <CardContent
                              sx={{
                                 p: 0.5,
                                 display: 'flex',
                                 justifyContent: 'flex-start',
                                 alignItems: 'center',
                              }}
                           >
                              <PhotoLikeButton
                                 photoId={photo.originalPhotoId}
                                 userId={currentUser?.uid || ''}
                                 context={{ type: 'collection', id: collection?.id || '' }}
                                 initialLikeCount={photo.likeCount || 0}
                                 initialIsLiked={photo.isLiked || false}
                                 size='small'
                                 variant='compact'
                                 showCount={true}
                                 disabled={!currentUser}
                                 onLikeChange={(isLiked, likeCount) => {
                                    // Update the photo in the collection state
                                    setCollection((prev) => {
                                       if (!prev) return prev;
                                       const updatedPhotos = prev.photos.map((p) =>
                                          p.originalPhotoId === photo.originalPhotoId
                                             ? { ...p, isLiked, likeCount }
                                             : p
                                       );
                                       return { ...prev, photos: updatedPhotos };
                                    });
                                 }}
                              />
                           </CardContent>
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            );
      }
   };

   const getEntityTypeIcon = (entityType: string) => {
      switch (entityType) {
         case 'trip':
            return '✈️';
         case 'event':
            return '📅';
         case 'album':
            return '📸';
         default:
            return '📁';
      }
   };

   if (loading) {
      return (
         <Container maxWidth='lg'>
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   if (error) {
      return (
         <Container maxWidth='lg'>
            <Box p={3}>
               <Alert severity='error'>{error}</Alert>
               <Box mt={2} textAlign='center'>
                  <Button variant='contained' onClick={() => navigate('/')}>
                     Go to Home
                  </Button>
               </Box>
            </Box>
         </Container>
      );
   }

   if (!collection) {
      return (
         <Container maxWidth='lg'>
            <Box p={3}>
               <Alert severity='warning'>Collection not found</Alert>
            </Box>
         </Container>
      );
   }

   return (
      <Container maxWidth='lg'>
         <Box py={2}>
            {/* Header */}
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
               <Box display='flex' alignItems='center' gap={2} mb={2}>
                  <Typography variant='body1'>
                     {getEntityTypeIcon(collection.entityType)}
                  </Typography>
                  <Typography variant='h4'>
                     {collection.title ||
                        collection.subject ||
                        `Photos from ${collection.entityTitle}`}
                  </Typography>
               </Box>

               <Typography variant='body2' color='textSecondary' gutterBottom>
                  Shared on {format(collection.createdAt, 'MMMM dd, yyyy')}
               </Typography>

               {collection.message && (
                  <Box mt={2} p={2} bgcolor='grey.50' borderRadius={1}>
                     <Typography variant='body1' style={{ whiteSpace: 'pre-wrap' }}>
                        {collection.message}
                     </Typography>
                  </Box>
               )}

               <Box mt={2} display='flex' gap={1} alignItems='center'>
                  <Chip label={`${collection.photos.length} photos`} color='primary' size='small' />
               </Box>

               {/* Action buttons for non-users */}
               {!currentUser && (
                  <Box mt={3} display='flex' gap={2}>
                     <Button
                        variant='contained'
                        startIcon={<PersonAddIcon />}
                        onClick={handleJoinWetrippen}
                     >
                        Join wetrippen
                     </Button>
                     <Button variant='outlined' startIcon={<LoginIcon />} onClick={handleLogin}>
                        Login
                     </Button>
                  </Box>
               )}
            </Paper>

            {/* Photo Grid */}
            <Box>{renderPhotoGrid()}</Box>

            {/* Photo Lightbox */}
            <Dialog
               open={selectedPhotoIndex !== null}
               onClose={() => setSelectedPhotoIndex(null)}
               maxWidth='lg'
               fullWidth
            >
               <DialogContent sx={{ p: 0, position: 'relative' }}>
                  {selectedPhotoIndex !== null && collection.photos[selectedPhotoIndex] && (
                     <>
                        <IconButton
                           sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              zIndex: 1,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                           }}
                           onClick={() => setSelectedPhotoIndex(null)}
                        >
                           <CloseIcon />
                        </IconButton>
                        <img
                           src={collection.photos[selectedPhotoIndex].url}
                           alt={collection.photos[selectedPhotoIndex].title}
                           style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '90vh',
                              objectFit: 'contain',
                           }}
                        />
                        {collection.photos[selectedPhotoIndex].title && (
                           <Box p={2}>
                              <Typography variant='h6'>
                                 {collection.photos[selectedPhotoIndex].title}
                              </Typography>
                              {collection.photos[selectedPhotoIndex].description && (
                                 <Typography variant='body2' color='textSecondary'>
                                    {collection.photos[selectedPhotoIndex].description}
                                 </Typography>
                              )}
                           </Box>
                        )}
                     </>
                  )}
               </DialogContent>
            </Dialog>

            {/* Email Collection Dialog */}
            <Dialog
               open={showEmailDialog}
               onClose={() => {}}
               maxWidth='sm'
               fullWidth
               disableEscapeKeyDown
               aria-labelledby='email-dialog-title'
            >
               <DialogTitle id='email-dialog-title'>
                  <Box display='flex' alignItems='center' gap={1}>
                     <EmailIcon color='primary' />
                     <Typography variant='h6'>Access Required</Typography>
                  </Box>
               </DialogTitle>
               <DialogContent>
                  <Typography variant='body1' gutterBottom>
                     To view this photo collection, please enter your email address.
                  </Typography>
                  <Typography variant='body2' color='textSecondary' gutterBottom sx={{ mb: 3 }}>
                     We'll check if you have an account and redirect you to either login or
                     register.
                  </Typography>
                  <TextField
                     autoFocus
                     margin='dense'
                     label='Email Address'
                     type='email'
                     fullWidth
                     variant='outlined'
                     value={viewerEmail}
                     onChange={(e) => setViewerEmail(e.target.value)}
                     onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                           handleEmailSubmit();
                        }
                     }}
                     error={!!emailError}
                     helperText={emailError}
                     placeholder='your.email@example.com'
                  />
               </DialogContent>
               <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={handleSkipEmail} color='inherit'>
                     I'm New Here
                  </Button>
                  <Button
                     onClick={handleEmailSubmit}
                     variant='contained'
                     disabled={!viewerEmail.trim()}
                  >
                     Continue
                  </Button>
               </DialogActions>
            </Dialog>

            {/* Floating Signup Button - Only show for non-authenticated users */}
            {!currentUser && (
               <Fab
                  variant='extended'
                  color='primary'
                  onClick={handleJoinWetrippen}
                  sx={{
                     position: 'fixed',
                     bottom: 24,
                     right: 24,
                     zIndex: 1000,
                     boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                     background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                     color: 'white',
                     fontSize: '16px',
                     fontWeight: 'bold',
                     textTransform: 'none',
                     px: 3,
                     py: 1.5,
                     minHeight: '56px',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        transform: 'scale(1.05)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                     },
                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                     animation: 'pulse 2s infinite',
                     '@keyframes pulse': {
                        '0%': {
                           boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        },
                        '50%': {
                           boxShadow: '0 8px 32px rgba(0, 196, 167, 0.4)',
                        },
                        '100%': {
                           boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        },
                     },
                  }}
               >
                  <PersonAddIcon sx={{ mr: 1 }} />
                  Join Wetrippen
               </Fab>
            )}
         </Box>
      </Container>
   );
};

export default PublicSharedCollection;
